// src/pages/auth/ForgotPassword.tsx
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Button from '../../components/common/Button';
import { RootState } from '../../store';
import { requestPasswordReset, clearError, clearResetMessages } from '../../store/slices/authSlice';
import { useAppDispatch } from '../../hooks/useAppDispatch';

const PageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colours.background.default};
`;

const ForgotPasswordCard = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  width: 100%;
  max-width: 450px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colours.text.secondary};
  text-align: center;
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #DDDDDD;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colours.primary};
    box-shadow: 0 0 0 1px ${({ theme }) => theme.colours.primary};
  }
`;

const ResetButton = styled(Button)`
  width: 100%;
  background-color: #1A0633;
  color: white;
  
  &:hover {
    background-color: #12042A;
  }
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  text-align: center;
  padding: 0.5rem;
  background-color: #ffebee;
  border-radius: 0.25rem;
  display: ${props => props.children ? 'block' : 'none'};
`;

const SuccessMessage = styled.div`
  color: #2e7d32;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  text-align: center;
  padding: 0.5rem;
  background-color: #e8f5e9;
  border-radius: 0.25rem;
  display: ${props => props.children ? 'block' : 'none'};
`;

const BackToLogin = styled.div`
  margin-top: 1.5rem;
  text-align: center;
  font-size: 0.875rem;
`;

const StyledLink = styled(Link)`
  color: ${({ theme }) => theme.colours.primary};
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

interface ForgotPasswordFormData {
  email: string;
}

const ForgotPassword: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isLoading, error, resetRequestSuccess, resetRequestMessage } = useSelector(
    (state: RootState) => state.auth
  );

  const [formData, setFormData] = useState<ForgotPasswordFormData>({
    email: "",
  });

  // Clear any errors and reset messages on component mount
  useEffect(() => {
    dispatch(clearError());
    dispatch(clearResetMessages());
  }, [dispatch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Dispatch password reset request action with form data
    await dispatch(requestPasswordReset({
      email: formData.email,
      frontend_url: `${window.location.origin}/reset-password`,
    }));
  };

  return (
    <PageContainer>
      <ForgotPasswordCard>
        <Title>Forgot Password</Title>
        <Subtitle>Please provide your email address to reset your password.</Subtitle>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {resetRequestSuccess && <SuccessMessage>{resetRequestMessage || "Password reset link has been sent to your email."}</SuccessMessage>}
        
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="email">Email Address</Label>
            <Input
              type="email"
              id="email"
              name="email"
              placeholder="Enter Email Address"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </FormGroup>
          
          <ResetButton 
            type="submit" 
            size="large" 
            disabled={isLoading || resetRequestSuccess}
          >
            {isLoading ? 'Sending request...' : 'Reset Password'}
          </ResetButton>
        </form>
        
        <BackToLogin>
          Remember your password? <StyledLink to="/login">Back to login</StyledLink>
        </BackToLogin>
      </ForgotPasswordCard>
    </PageContainer>
  );
};

export default ForgotPassword;